import React, { useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    setCabinetProfilePersonalInformationEditModalStatus, setProfilePersonalInformation
} from "../../../store/cabinet/action";
import {
    getCountries,
    getCountryCallingCode
} from "react-phone-number-input";
import {toast} from "react-toastify";

const initialFormData = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    phone_country: "",
    phone_code: "",
};

const getCountryName = (countryCode, language = "en") => {
    try {
        const names = new Intl.DisplayNames([language], { type: "region" });

        return names.of(countryCode) || countryCode;
    } catch (error) {
        return countryCode;
    }
};

const getCountryOptions = (language = "en") => {
    return getCountries()
        .map((countryCode) => {
            const phoneCode = `+${getCountryCallingCode(countryCode)}`;

            return {
                code: countryCode,
                name: getCountryName(countryCode, language),
                englishName: getCountryName(countryCode, "en"),
                phoneCode,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
};

const getCountryByValue = (countries, value) => {
    if (!value) return null;

    return countries.find((country) => {
        return (
            country.code === value ||
            country.name === value ||
            country.englishName === value ||
            country.phoneCode === value
        );
    }) || null;
};

const normalizePhone = (phone = "", phoneCode = "") => {
    if (!phone) return "";

    const phoneValue = String(phone).trim();

    if (phoneCode && phoneValue.startsWith(phoneCode)) {
        return phoneValue.replace(phoneCode, "").trim();
    }

    return phoneValue;
};

const CabinetProfilePersonalInformationEditModal = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    const modalStatus = useSelector(store => store.cabinet.cabinetProfilePersonalInformationEditModalStatus);
    const profileMainData = useSelector(store => store.cabinet.profileMainData);
    // const formErr = useSelector(store => store.cabinet.profilePersonalInformationEditStatusErr);
    // const profilePersonalInformationEditStatusStarted = useSelector(store => store.cabinet.profilePersonalInformationEditStatusStarted);



    const [formData, setFormData] = useState(initialFormData);

    const countries = useMemo(() => {
        return getCountryOptions(i18n.language || "en");
    }, [i18n.language]);

    const selectedCountry = useMemo(() => {
        return getCountryByValue(countries, formData.country);
    }, [countries, formData.country]);

    const selectedPhoneCountry = useMemo(() => {
        return getCountryByValue(countries, formData.phone_country);
    }, [countries, formData.phone_country]);

    const closeModal = () => {
        dispatch(setCabinetProfilePersonalInformationEditModalStatus(false));
    };

    const inputChangeHandler = (event) => {
        const { name, value } = event.target;

        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const countryChangeHandler = (event) => {
        const countryCode = event.target.value;
        const country = getCountryByValue(countries, countryCode);

        setFormData((prevState) => ({
            ...prevState,
            country: country?.code || "",
            phone_country: country?.code || prevState.phone_country,
            phone_code: country?.phoneCode || prevState.phone_code,
        }));
    };

    const phoneCountryChangeHandler = (event) => {
        const countryCode = event.target.value;
        const country = getCountryByValue(countries, countryCode);

        setFormData((prevState) => ({
            ...prevState,
            phone_country: country?.code || "",
            phone_code: country?.phoneCode || "",
        }));
    };

    const phoneChangeHandler = (event) => {
        const value = event.target.value;

        if (!/^[0-9\s\-()]*$/.test(value)) return;

        setFormData((prevState) => ({
            ...prevState,
            phone: value,
        }));
    };

    const successHandler = () => {
        closeModal();
        toast(t('Ваши данные успешно обновлены!'))
    }

    const confirmHandler = () => {
        const phoneCode = selectedPhoneCountry?.phoneCode || formData.phone_code || "";
        const clearPhone = formData.phone.trim();

        const fullPhone = clearPhone
            ? `${phoneCode} ${clearPhone}`.trim()
            : null;

        const payload = {
            first_name: formData.first_name.trim() || null,
            last_name: formData.last_name.trim() || null,
            phone: fullPhone,
            phone_country: selectedPhoneCountry?.code || null,
            phone_code: phoneCode || null,
            country: selectedCountry?.englishName || selectedCountry?.name || null,
            country_code: selectedCountry?.code || null,
        };

        dispatch(setProfilePersonalInformation(payload, successHandler));
    };

    useEffect(() => {
        if (!modalStatus) {
            setFormData(initialFormData);
            return;
        }

        const defaultCountry =
            getCountryByValue(countries, profileMainData?.country) ||
            getCountryByValue(countries, profileMainData?.country_code) ||
            getCountryByValue(countries, "US") ||
            countries[0] ||
            null;

        const phoneCountry =
            getCountryByValue(countries, profileMainData?.phone_country) ||
            getCountryByValue(countries, profileMainData?.phone_country_code) ||
            defaultCountry;

        const phoneCode = phoneCountry?.phoneCode || defaultCountry?.phoneCode || "";

        setFormData({
            first_name: profileMainData?.first_name || "",
            last_name: profileMainData?.last_name || "",
            email: profileMainData?.email || "",
            phone: normalizePhone(profileMainData?.phone || "", phoneCode),
            country: defaultCountry?.code || "",
            phone_country: phoneCountry?.code || "",
            phone_code: phoneCode,
        });
    }, [modalStatus, profileMainData, countries]);

    return (
        <Popup
            open={!!modalStatus}
            modal
            nested
            closeOnDocumentClick={true}
            onClose={closeModal}
            className="cabinetProfilePersonalInformationEditModal"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={Styles.modalCloseBtn}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.titleRow}>
                        <div className={Styles.titleIcon}>
                            {/* icon here */}
                        </div>

                        <div className={Styles.titleTextWrap}>
                            <div className={Styles.title}>
                                {t("Редактировать личную информацию")}
                            </div>

                            <div className={Styles.note}>
                                {t("Информация аккаунта")}
                            </div>
                        </div>
                    </div>

                    <div className={Styles.formWrap}>
                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="profile-first-name"
                            >
                                {t("Имя")}
                            </label>

                            <input
                                id="profile-first-name"
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={inputChangeHandler}
                                className={Styles.formInput}
                                placeholder={t("Введите имя")}
                            />
                        </div>

                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="profile-last-name"
                            >
                                {t("Фамилия")}
                            </label>

                            <input
                                id="profile-last-name"
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={inputChangeHandler}
                                className={Styles.formInput}
                                placeholder={t("Введите фамилию")}
                            />
                        </div>

                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="profile-email"
                            >
                                {t("Email")}
                            </label>

                            <input
                                id="profile-email"
                                type="email"
                                name="email"
                                value={formData.email}
                                className={Styles.formInput}
                                placeholder={t("Email")}
                                disabled
                                readOnly
                            />

                            <div className={Styles.formHint}>
                                {t("Email нельзя изменить")}
                            </div>
                        </div>

                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="profile-phone"
                            >
                                {t("Номер телефона")}
                            </label>

                            <div className={Styles.phoneField}>
                                <div className={Styles.phoneCountrySelectWrap}>
                                    <select
                                        value={formData.phone_country}
                                        onChange={phoneCountryChangeHandler}
                                        className={Styles.phoneCodeSelect}
                                        aria-label={t("Код страны")}
                                    >
                                        {countries.map((country) => (
                                            <option
                                                key={country.code}
                                                value={country.code}
                                            >
                                                {country.phoneCode} · {country.name}
                                            </option>
                                        ))}
                                    </select>

                                    <div className={Styles.phoneSelectArrow} />
                                </div>

                                <input
                                    id="profile-phone"
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={phoneChangeHandler}
                                    className={Styles.phoneInput}
                                    placeholder={t("Введите номер телефона")}
                                    inputMode="tel"
                                />
                            </div>
                        </div>

                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="profile-country"
                            >
                                {t("Страна")}
                            </label>

                            <div className={Styles.selectWrap}>
                                <select
                                    id="profile-country"
                                    name="country"
                                    value={formData.country}
                                    onChange={countryChangeHandler}
                                    className={Styles.formSelect}
                                >
                                    {countries.map((country) => (
                                        <option
                                            key={country.code}
                                            value={country.code}
                                        >
                                            {country.name}
                                        </option>
                                    ))}
                                </select>

                                <div className={Styles.selectArrow} />
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={Styles.confirmBtn}
                        onClick={confirmHandler}
                    >
                        {t("Подтвердить")}
                    </button>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetProfilePersonalInformationEditModal;
